"use client";

// #468 catalog builder bar (mockup: catalog_box_bar_mockup.html). ONE compact,
// slim bar shared by BOTH catalog views (Power Buyer + Magazine) - it replaces
// the stacked add-toasts, the old empty-cart obstacle, and the per-card
// QuickAddToast. Layout matches the approved mockup:
//   [cart glyph+badge] [summary] [current-box fill bar] [size pills] [View cart]
// on one ~52px desktop line; on phones it collapses to ONE ~46px line (glyph,
// "Box N · QB · X of N · +M", mini progress, caret, Cart) and the size pills
// expand on tap. All fill/size math stays in box-rules (computeBoxFill) and the
// pills are the SAME shared <CartBoxSizeSelector> the cart uses; colors are the
// portal tokens. The "last add" comes from the shared catalog-feedback store so
// any card/row add drives this single bar. Empty cart -> no bar.
import { useState } from "react";
import Link from "next/link";
import { useCartStore } from "@/stores/cart";
import { useCatalogFeedback, type LastAdd } from "@/stores/catalog-feedback";
import { computeBoxFill } from "@/lib/cart/box-rules";
import { sizeWord, spelledBoxSize } from "@/lib/cart/box-size-label";
import { CartBoxSizeSelector } from "@/components/cart/CartBoxSizeSelector";
import { CartIconButton } from "@/components/cart/CartIconButton";
import type { CartBox } from "@/lib/cart/types";

export type { LastAdd };

function boxHasSlug(box: CartBox, slug: string): boolean {
  return box.contents.some((l) => l.slug === slug);
}

// A short, human box name for the fill label: the dominant (most-stems) line's
// variety name, tagged "mix" when the box holds more than one variety.
function boxName(box: CartBox): string {
  const dom = [...box.contents].sort((a, b) => b.stems - a.stems)[0];
  if (!dom) return "Box";
  const allSame = box.contents.every((l) => l.variety === dom.variety);
  return allSame ? dom.variety : `${dom.variety} mix`;
}

export function CartBuilderBar() {
  const cart = useCartStore((s) => s.legacyCart);
  // lastAdd only steers WHICH box the bar surfaces (the one the add landed in);
  // the bar itself is the confirmation, so there is no separate flash/toast.
  const lastAdd = useCatalogFeedback((s) => s.lastAdd);
  const [expanded, setExpanded] = useState(false);

  const boxes = cart.boxes;
  if (boxes.length === 0) return null; // empty cart -> no bar

  const totalBunches = boxes.reduce(
    (n, b) => n + b.contents.reduce((x, l) => x + (l.unit === "bunch" ? l.quantity : 0), 0),
    0,
  );
  // #63 box_stem: loose stems (hydrangea by-the-stem) counted for the summary so
  // a stem cart reads "40 stems", not "0 bunches".
  const totalStems = boxes.reduce(
    (n, b) => n + b.contents.reduce((x, l) => x + (l.unit === "stem" ? l.quantity : 0), 0),
    0,
  );
  const unitsLabel =
    totalBunches > 0 && totalStems > 0
      ? `${totalBunches} ${totalBunches === 1 ? "bunch" : "bunches"} · ${totalStems} stems`
      : totalStems > 0
        ? `${totalStems} ${totalStems === 1 ? "stem" : "stems"}`
        : `${totalBunches} ${totalBunches === 1 ? "bunch" : "bunches"}`;

  const current =
    (lastAdd?.slug && [...boxes].reverse().find((b) => boxHasSlug(b, lastAdd.slug!))) ||
    boxes[boxes.length - 1];
  const currentIndex = boxes.indexOf(current);
  const fill = computeBoxFill(current);
  const sizeCode = fill.selectedSize ?? current.size;

  const used = fill.bunchesUsed;
  const target = fill.bunchTarget;
  const needed = fill.bunchesNeeded;
  const hasBunchTarget = fill.mode === "bunch" && target > 0;
  // #63 box_stem: a stem-fill box (mode "stems" WITH a populated EB/QB ladder)
  // shows progress in STEMS ("30 of 40 stems · add 10 more"), reusing this same
  // bar. A discrete whole-box hydrangea box has an empty ladder -> no target.
  const hasStemTarget =
    fill.mode === "stems" && fill.ladder.length >= 2 && fill.stemsTarget > 0;
  const stemUsed = fill.stemsUsed;
  const stemTarget = fill.stemsTarget;
  const stemNeeded = fill.stemsNeeded;

  // Desktop / mobile right-side "need" text.
  const desktopNeed = fill.full
    ? "complete"
    : hasBunchTarget
      ? `${used} of ${target} · add ${needed} more`
      : hasStemTarget
        ? `${stemUsed} of ${stemTarget} stems · add ${stemNeeded} more`
        : "";
  const mobileNeed = fill.full
    ? "ready"
    : hasBunchTarget
      ? `+${needed}`
      : hasStemTarget
        ? `+${stemNeeded}`
        : "";

  return (
    <div
      data-testid="cart-builder-bar"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-3 pb-3 md:px-4 md:pb-4"
    >
      <div className="pointer-events-auto mx-auto max-w-5xl rounded-xl border border-brand-border bg-white shadow-[0_2px_10px_rgba(0,0,0,0.10)]">
        <div className="flex items-center gap-2.5 px-3 py-1.5 md:h-[52px] md:gap-3.5 md:px-3.5">
          {/* Glyph + count badge = the header's cart button (same component). */}
          <CartIconButton className="shrink-0" />

          {/* Summary (desktop only). */}
          <span
            data-testid="cart-builder-totals"
            className="hidden shrink-0 whitespace-nowrap text-[13px] text-brand-text-muted md:inline"
          >
            <strong className="font-semibold text-brand-text">{unitsLabel}</strong> ·{" "}
            <strong data-testid="cart-builder-boxcount" className="font-semibold text-brand-text">
              {boxes.length}
            </strong>{" "}
            {boxes.length === 1 ? "box" : "boxes"} ·{" "}
            <strong className="font-semibold text-brand-text">${cart.subtotal.toFixed(2)}</strong>
          </span>

          {/* Current-box fill (label + track). */}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-[12px]">
              <span
                data-testid="cart-builder-box-label"
                className="truncate font-semibold text-brand-text"
              >
                {/* Mobile: "Box 2 · Standard · 2 of 15" - Desktop: "Box 2 · Roses - standard box" */}
                <span className="md:hidden">
                  Box {currentIndex + 1} · {sizeWord(sizeCode)}
                  {hasBunchTarget
                    ? ` · ${used} of ${target}`
                    : hasStemTarget
                      ? ` · ${stemUsed} of ${stemTarget}`
                      : ""}
                </span>
                <span className="hidden md:inline">
                  Box {currentIndex + 1} · {boxName(current)} - {spelledBoxSize(sizeCode)}
                </span>
              </span>
              <span className="shrink-0 whitespace-nowrap text-brand-text-muted">
                <span className="md:hidden">{mobileNeed}</span>
                <span className="hidden md:inline">{desktopNeed}</span>
              </span>
            </div>
            <div
              className="h-[5px] overflow-hidden rounded-full bg-brand-teal-soft"
              aria-hidden="true"
            >
              <div
                data-testid="cart-builder-fill-bar"
                data-fill-pct={fill.fillPct}
                className={fill.full ? "h-full rounded-full bg-brand-success" : "h-full rounded-full bg-brand-teal"}
                style={{ width: `${fill.fillPct}%` }}
              />
            </div>
          </div>

          {/* Size pills (desktop inline). */}
          <div className="hidden shrink-0 md:block">
            <CartBoxSizeSelector box={current} fill={fill} variant="inline" />
          </div>

          {/* Caret (mobile only) - toggles the size pills. */}
          {fill.ladder.length >= 2 && (
            <button
              type="button"
              data-testid="cart-builder-caret"
              aria-label={expanded ? "Hide box sizes" : "Choose box size"}
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
              className="shrink-0 px-1 text-[12px] text-brand-text-muted md:hidden"
            >
              {expanded ? "▴" : "▾"}
            </button>
          )}

          <Link
            href="/cart"
            data-testid="cart-builder-view"
            className="shrink-0 self-center whitespace-nowrap rounded-lg bg-brand-teal px-3.5 py-2 text-center text-[13px] font-semibold text-white hover:bg-brand-teal/90 md:px-4"
          >
            <span className="md:hidden">Cart</span>
            <span className="hidden md:inline">View cart</span>
          </Link>
        </div>

        {/* Mobile expanded: size pills appear on tap (still small). */}
        {expanded && (
          <div className="border-t border-brand-border px-3 pb-2 pt-2 md:hidden">
            <CartBoxSizeSelector box={current} fill={fill} variant="inline" />
          </div>
        )}
      </div>
    </div>
  );
}
